import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { dateTime, number } from "../lib/format";
import { ui } from "../lib/ui";

interface Movimiento {
  id: number;
  fecha: string;
  producto_id: number;
  tipo: string;
  cantidad: number;
  stock_resultante: number;
  motivo?: string | null;
}

interface Producto {
  id: number;
  sku: string;
  nombre: string;
}

const TIPOS = ["ENTRADA", "SALIDA", "MERMA", "AJUSTE"] as const;

const badge = (tipo: string) => {
  const map: Record<string, string> = {
    ENTRADA: "bg-emerald-50 text-emerald-700",
    SALIDA: "bg-rose-50 text-rose-700",
    MERMA: "bg-orange-50 text-orange-700",
    AJUSTE: "bg-slate-100 text-slate-700",
    VENTA: "bg-blue-50 text-blue-700",
  };
  return map[tipo] ?? "bg-slate-100 text-slate-700";
};

export default function Inventario() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["inventario", "movimientos"],
    queryFn: async () => {
      const { data } = await api.get<Movimiento[]>("/api/inventory/movements", {
        params: { limit: 100 },
      });
      return data;
    },
  });

  const { data: productos } = useQuery({
    queryKey: ["productos", "all-for-inv"],
    queryFn: async () => {
      const { data } = await api.get<Producto[]>("/api/products", {
        params: { limit: 500 },
      });
      return data;
    },
  });

  const productMap = new Map(productos?.map((p) => [p.id, p]) ?? []);

  return (
    <div className="flex-1 flex flex-col h-full">
      <header className={ui.header}>
        <div>
          <h1 className={ui.title}>Inventario</h1>
          <p className={ui.subtitle}>Movimientos de stock</p>
        </div>
        <button className={ui.btnPrimary} onClick={() => setCreating(true)}>
          + Movimiento
        </button>
      </header>

      <div className={ui.page}>
        {error && <div className={ui.error}>Error al cargar movimientos.</div>}

        <div className={ui.card}>
          <table className={ui.table}>
            <thead className={ui.thead}>
              <tr>
                <th className={ui.th}>Fecha</th>
                <th className={ui.th}>Producto</th>
                <th className={ui.th}>Tipo</th>
                <th className={ui.th + " text-right"}>Cantidad</th>
                <th className={ui.th + " text-right"}>Stock resultante</th>
                <th className={ui.th}>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {isLoading &&
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td colSpan={6} className="p-3">
                      <div className={ui.skeleton + " h-6 w-full"} />
                    </td>
                  </tr>
                ))}
              {!isLoading &&
                data?.map((m) => {
                  const p = productMap.get(m.producto_id);
                  return (
                    <tr key={m.id} className="border-b border-slate-100">
                      <td className={ui.td}>{dateTime(m.fecha)}</td>
                      <td className={ui.td + " font-medium text-slate-900"}>
                        {p ? (
                          <>
                            <span className="font-mono text-xs text-slate-500 mr-2">
                              {p.sku}
                            </span>
                            {p.nombre}
                          </>
                        ) : (
                          `#${m.producto_id}`
                        )}
                      </td>
                      <td className={ui.td}>
                        <span className={ui.badge + " " + badge(m.tipo)}>
                          {m.tipo}
                        </span>
                      </td>
                      <td
                        className={
                          ui.td +
                          " text-right font-semibold " +
                          (Number(m.cantidad) < 0
                            ? "text-rose-600"
                            : "text-emerald-600")
                        }
                      >
                        {Number(m.cantidad) > 0 ? "+" : ""}
                        {number(m.cantidad, 2)}
                      </td>
                      <td className={ui.td + " text-right font-bold"}>
                        {number(m.stock_resultante, 2)}
                      </td>
                      <td className={ui.td + " text-slate-500"}>
                        {m.motivo || "—"}
                      </td>
                    </tr>
                  );
                })}
              {!isLoading && !data?.length && (
                <tr>
                  <td colSpan={6} className={ui.empty}>
                    Sin movimientos.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <MovimientoModal
          productos={productos ?? []}
          onClose={() => setCreating(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["inventario"] });
            qc.invalidateQueries({ queryKey: ["productos"] });
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}

function MovimientoModal({
  productos,
  onClose,
  onSaved,
}: {
  productos: Producto[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [productoId, setProductoId] = useState<number | "">("");
  const [tipo, setTipo] = useState<(typeof TIPOS)[number]>("ENTRADA");
  const [cantidad, setCantidad] = useState<number>(0);
  const [motivo, setMotivo] = useState("");

  const m = useMutation({
    mutationFn: async () => {
      await api.post("/api/inventory/movements", {
        producto_id: productoId,
        tipo,
        cantidad,
        motivo: motivo || undefined,
      });
    },
    onSuccess: onSaved,
  });

  return (
    <div className={ui.modalOverlay} onClick={onClose}>
      <div className={ui.modalCard} onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-bold text-slate-900 mb-4">
          Nuevo movimiento
        </h2>
        <div className="space-y-3">
          <div>
            <label className={ui.label}>Producto</label>
            <select
              className={ui.input}
              value={productoId}
              onChange={(e) =>
                setProductoId(e.target.value ? Number(e.target.value) : "")
              }
            >
              <option value="">— Elegir —</option>
              {productos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.sku} — {p.nombre}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={ui.label}>Tipo</label>
            <select
              className={ui.input}
              value={tipo}
              onChange={(e) => setTipo(e.target.value as (typeof TIPOS)[number])}
            >
              {TIPOS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            {tipo === "AJUSTE" && (
              <p className="text-xs text-slate-500 mt-1">
                En AJUSTE la cantidad es el stock final.
              </p>
            )}
          </div>
          <div>
            <label className={ui.label}>Cantidad</label>
            <input
              type="number"
              step="0.01"
              className={ui.input}
              value={cantidad}
              onChange={(e) => setCantidad(parseFloat(e.target.value) || 0)}
            />
          </div>
          <div>
            <label className={ui.label}>Motivo</label>
            <textarea
              className={ui.input}
              rows={2}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </div>
        </div>

        {m.isError && (
          <div className={ui.error + " mt-3"}>Error registrando movimiento.</div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button className={ui.btnSecondary} onClick={onClose}>
            Cancelar
          </button>
          <button
            className={ui.btnPrimary}
            disabled={m.isPending || !productoId || cantidad === 0}
            onClick={() => m.mutate()}
          >
            {m.isPending ? "Guardando..." : "Registrar"}
          </button>
        </div>
      </div>
    </div>
  );
}
